/**
 * Deadman Brewery — theme.js
 * Global entry point. Imports and initialises all interactive modules.
 */

import CartDrawer from './cart-drawer.js';
import AgeGate from './age-gate.js';
import MobileNav from './mobile-nav.js';
import HeaderAccountMenu from './header-account-menu.js';
import { initPageA11y, exposePageA11yGlobals } from './page-a11y.js';
import { initBeerStyleGridScrollLink } from './beer-style-grid-scroll.js';

function initHeaderIntroAnimation() {
  const header = document.querySelector('.site-header');
  if (!header) return;

  const reduceMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (reduceMotion) {
    header.classList.add('site-header--intro-ready');
    return;
  }

  // Next frame ensures styles are applied before transitioning.
  requestAnimationFrame(() => {
    header.classList.add('site-header--intro-ready');
  });
}

function initAnnouncementBarScrollHide() {
  // Target the outer <aside class="announcement-bar-wrapper"> that Shopify renders.
  const wrapper = document.querySelector('.announcement-bar-wrapper');
  if (!wrapper) return;
  // Only activate if there is actually an announcement bar inside.
  if (!wrapper.querySelector('[data-announcement-bar]')) return;

  const reduceMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const topThreshold = 8; // px — treat as "at top of page"
  let isHidden = false;

  // Cache the natural rendered height so we can restore it.
  // Keep explicit px height always set so CSS can transition it.
  let barHeight = wrapper.offsetHeight;
  wrapper.style.height = barHeight + 'px';

  const updateHeight = () => {
    if (!isHidden) {
      // Re-measure in case viewport width changed.
      wrapper.style.height = 'auto';
      barHeight = wrapper.offsetHeight;
      wrapper.style.height = barHeight + 'px';
    }
  };

  const hide = () => {
    if (isHidden) return;
    isHidden = true;
    if (reduceMotion) {
      wrapper.style.height = '0';
      return;
    }
    // Ensure explicit height is set, then collapse to 0.
    wrapper.style.height = barHeight + 'px';
    // Double rAF ensures the browser registers the start value before transitioning.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      wrapper.style.height = '0';
    }));
  };

  const show = () => {
    if (!isHidden) return;
    isHidden = false;
    if (reduceMotion) {
      wrapper.style.height = barHeight + 'px';
      return;
    }
    wrapper.style.height = barHeight + 'px';
  };

  let ticking = false;
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      const y = window.scrollY || window.pageYOffset || 0;
      if (y <= topThreshold) show();
      else hide();
      ticking = false;
    });
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', updateHeight, { passive: true });

  // Run once on load to set the correct initial state.
  onScroll();
}

function initScrollRevealSections() {
  const roots = document.querySelectorAll(
    '[data-hero-full], [data-beer-style-grid], [data-deadman-parallax-banner], [data-origin-story], [data-collection-hero]'
  );
  if (!roots.length) return;

  const reveal = (el) => {
    el.classList.add('is-visible');
  };

  const reduceMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (reduceMotion || !('IntersectionObserver' in window)) {
    roots.forEach(reveal);
    return;
  }

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          reveal(entry.target);
          io.unobserve(entry.target);
        }
      }
    },
    { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
  );

  roots.forEach((el) => io.observe(el));

  /* Hero and collection hero are often in view on first paint */
  for (const el of roots) {
    if (!el.matches('[data-hero-full], [data-collection-hero]')) continue;
    const r = el.getBoundingClientRect();
    if (r.top < window.innerHeight && r.bottom > 0) {
      reveal(el);
      io.unobserve(el);
    }
  }
}

function initCollectionHeroParallax() {
  const heroes = document.querySelectorAll('.collection-hero--parallax-enabled[data-collection-hero]');
  if (!heroes.length) return;

  const reduceMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduceMotion) return;

  const desktopMq =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(min-width: 900px)')
      : null;

  const update = () => {
    const useParallax = !desktopMq || desktopMq.matches;

    for (const hero of heroes) {
      const bg = hero.querySelector('[data-collection-hero-bg]');
      if (!bg) continue;

      if (!useParallax) {
        bg.style.transform = '';
        continue;
      }

      const rect = hero.getBoundingClientRect();
      const scrolledInto = Math.max(0, -rect.top);
      const offset = scrolledInto * 0.45;

      bg.style.transform = `translate3d(0, ${offset}px, 0)`;
    }
  };

  window.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update, { passive: true });
  update();
}

document.addEventListener('DOMContentLoaded', () => {
  initPageA11y();
  exposePageA11yGlobals();
  new AgeGate();
  new CartDrawer();
  new MobileNav();
  new HeaderAccountMenu();
  initHeaderIntroAnimation();
  initAnnouncementBarScrollHide();
  initScrollRevealSections();
  initCollectionHeroParallax();
  initBeerStyleGridScrollLink();
});
